/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import axios from 'axios';
import studentPortalService from '../services/studentPortal.service';
import type { StudentWorkspace } from '../types/studentPortal';

interface StudentPortalContextValue {
  workspace: StudentWorkspace | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const StudentPortalContext = createContext<StudentPortalContextValue | null>(null);

export function StudentPortalProvider({ children }: { children: ReactNode }) {
  const [workspace, setWorkspace] = useState<StudentWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setWorkspace(await studentPortalService.getWorkspace());
    } catch (requestError: unknown) {
      const detail = axios.isAxiosError(requestError) ? requestError.response?.data?.detail : undefined;
      setError(typeof detail === 'string' ? detail : detail?.message || 'Could not load your workspace.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo(() => ({ workspace, loading, error, refresh }), [workspace, loading, error, refresh]);
  return <StudentPortalContext.Provider value={value}>{children}</StudentPortalContext.Provider>;
}

export function useStudentPortal() {
  const value = useContext(StudentPortalContext);
  if (!value) throw new Error('useStudentPortal must be used inside StudentPortalProvider');
  return value;
}
